<template>
    <v-data-table
        :headers="attributeHeaders"
        :items="attributeItems"
        :hide-default-footer="true"
        :items-per-page="-1"
        no-data-text="None"
        :row-props="colorRowItem"
    >
        <template v-slot:item.options="{ item }">
            {{ buildOptionsDisplay(item) }}
        </template>
        <template v-slot:item.type="{ item }">
            {{ item.allow_multiple_values ? "multiple " : ""}}{{ item.type }}
        </template>
        <template v-slot:item.flags ="{ item }">
            <span v-if="item.subordinate_to_attribute">
                Subordinate to {{ item.subordinate_to_attribute }}
            </span>
            <span v-else>
                <v-chip v-if="item.required" size="small" color="primary" class="ma-2">Required</v-chip>
                <v-chip v-if="item.hidden" size="small" color="gray" class="ma-2">Hidden</v-chip>
                <v-chip v-if="!isEditable(item)" size="small" color="warning" class="ma-2">Read only</v-chip>
                <v-chip v-if="item.automated" size="small" color="aqua" class="ma-2">Automated</v-chip>
            </span>
        </template>
        <template v-slot:item.edit="{ item }">
            <span v-if="_.isNil(item.subordinate_to_attribute)">
                <v-icon-btn @click="showEditAttributeForm(item)"><v-icon>$pencil</v-icon></v-icon-btn>
            </span>
        </template>
        <template v-slot:item.delete="{ item }">
            <span v-if="_.isNil(item.subordinate_to_attribute)">
            <v-icon-btn v-if="item.source !== 'template'"
                        @click="deleteAttribute(item)"><v-icon>$trash-can-outline</v-icon></v-icon-btn>
            </span>
        </template>
    </v-data-table>
</template>
<script setup lang="ts">
import { ref, watch } from "vue";
import _ from "lodash";
import { Attribute } from "@/services/things/models";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import {
    isBasicDropdownWithOptions,
    isEditable,
    isNumericWithUnitsAttribute,
    isRelationAttribute,
} from "@/lib/thingUtils";
import { AttributeAdminConfig } from "@/services/schemas/manageSchemasAPI";
import { isModified, SchemaChanges } from "@/lib/schemaUtils";

export type Props = {
    attributes: Attribute[] | AttributeAdminConfig[],
    isRegistrySchema?: boolean,
    allowUpdate: boolean | undefined,
    isTemplateSchema?: boolean
    schemaChanges?: SchemaChanges,
    attributeType?: "one-time" | "time-series";
}

const {
    attributes,
    isRegistrySchema = false,
    allowUpdate = true,
    isTemplateSchema = false,
    schemaChanges = null,
    attributeType = null,
} = defineProps<Props>();

const emit = defineEmits<{
    (e: "showEditAttributeForm", attribute: AttributeItem, attributeType: "one-time" | "time-series" | null);
    (e: "deleteAttribute", attribute: AttributeItem, attributeType: "one-time" | "time-series" | null);
}>()

const attributeHeaders = ref<VDataTableHeader[]>([
    {
        title: "Order",
        value: "display_order",
        align: "start",
        sortable: true,
        width: "1%",
    },
    {
        title: "Name",
        value: "name",
        align: "start",
        sortable: true,
        width: "10%",
    },
    {
        title: "Display Name",
        value: "display_name",
        align: "start",
        sortable: true,
        width: "10%",
    },
    {
        title: "Type",
        value: "type",
        align: "start",
        sortable: true,
        width: "5%",
    },
    {
        title: "Options",
        value: "options",
        align: "start",
        sortable: true,
        width: "5%",
    },
]);

export type AttributeItem = Attribute & {
    defaultValue: string;
    isRelation: boolean;
    isDropdown: boolean;
    isMaster: boolean;
    modified?: boolean;
};

const attributeItems = ref<AttributeItem[]>([]);

watch(
    () => attributes,
    () => {
        attributeItems.value = buildAttributeItems(attributes);
    },
    {
        immediate: true,
        // `deep: true` is required as a parameter to `watch` when watching
        // an array value in order to watch both re-assignments of the array
        // instance and updated elements in an instance of the array.
        deep: true
    }
);

watch(
    () => isRegistrySchema,
    () => buildHeaders(),
    { immediate: true }
);

function buildHeaders() {
    if (!isRegistrySchema) {
        attributeHeaders.value.push(
            {
                title: "Default",
                value: "defaultValue",
                align: "start",
                sortable: true,
                width: "5%",
            },
            {
                title: "Widget",
                value: "widget_type",
                align: "start",
                sortable: true,
                width: "5%",
            },
        );

        if (!isTemplateSchema) {
            attributeHeaders.value.push(
                {
                    title: "Source",
                    value: "source",
                    align: "start",
                    sortable: false,
                    width: "10%",
                },
            )
        }

        attributeHeaders.value.push(
            {
                title: "Flags",
                value: "flags",
                width: "2%"
            },
        );
    }

    if (allowUpdate) {
        attributeHeaders.value.push(
            {
                title: "Edit",
                value: "edit",
                align: "start",
                sortable: false,
                width: "5%",
            },
            {
                title: "Delete",
                value: "delete",
                align: "start",
                sortable: false,
                width: "5%",
            }
        );
    }
}

function buildAttributeItems(attributes: Attribute[]) {
    return attributes.map((attribute) => {
        return buildAttributeItem(attribute);
    });
}

function buildAttributeItem(attribute: Attribute): AttributeItem {
    return {
        ...attribute,
        defaultValue: buildDefaultValue(attribute),
        isRelation: !_.isNil(attribute.relation),
        isDropdown: false,
        isMaster: false,
    };
}

function colorRowItem(row: { item: Attribute }) {
    const attribute = row.item;
    if (schemaChanges && isModified(attribute, schemaChanges)) {
        return { class: 'modified' };
    } else {
        return null;
    }
}

function buildDefaultValue(attribute: Attribute): string {
    if (isNumericWithUnitsAttribute(attribute)) {
        const valueDefault = attribute.option_default;
        const unitsDefault = attribute.value_default;
        if (_.isNil(valueDefault)) {
            return unitsDefault || "";
        } else if (_.isNil(unitsDefault)) {
            return valueDefault;
        } else {
            return `${valueDefault}, ${unitsDefault}`;
        }
    } else {
        return `${attribute.option_default || ""}`;
    }
}

function showEditAttributeForm(attributeItem: AttributeItem) {
    emit("showEditAttributeForm", attributeItem, attributeType);
}

function deleteAttribute(attributeItem: AttributeItem) {
    emit("deleteAttribute", attributeItem, attributeType);
}

function buildOptionsDisplay(attribute: Attribute) {
    if (isBasicDropdownWithOptions(attribute) || isNumericWithUnitsAttribute(attribute)) {
        const s = attribute.options!.join(", ");
        if (s.length > 120) {
            return s.slice(0, 120) + "...";
        } else {
            return s;
        }
    } else if (isRelationAttribute(attribute)) {
        return attribute.relation!;
    } else {
        return "";
    }
}

</script>